import mixpanel from 'mixpanel-browser';
import posthog from 'posthog-js';
import config, { isProduction } from 'config';
// NODE_ENV is 'development' locally (npm start), 'test' in jest (npm run test-basic),
// and 'production' for all deployed builds (staging + production)
const env = process.env.NODE_ENV;
const skipTracking = env !== 'production';

// PostHog session replay records in production by default. In staging/local it
// stays off unless a developer opts in from the browser console with:
//   localStorage.setItem('pf_posthog_replay', 'on'); location.reload();
// Force it off anywhere (e.g. prod) with 'off'.
function posthogShouldRecord() {
    let override = null;
    try {
        override = localStorage.getItem('pf_posthog_replay'); // 'on' | 'off' | null
    } catch (e) {
        // localStorage may be unavailable (e.g. privacy mode) — fall back to env default
    }
    return override === 'on' || (isProduction && override !== 'off');
}

const posthogOptions = {
    api_host: config.POSTHOG_HOST,
    autocapture: true, // capture clicks/inputs/submits (also powers rage clicks + clickmaps)
    rageclick: true, // detect rapid repeated clicks = frustration
    capture_dead_clicks: true, // clicks that do nothing (looks clickable, isn't)
    capture_exceptions: true, // JS errors -> Error Tracking
    enable_heatmaps: true, // click/scroll heatmaps
    capture_performance: { web_vitals: true }, // page speed: LCP/CLS/INP/FCP
    capture_pageview: 'history_change', // SPA pageviews (needed for funnels/paths)
    capture_pageleave: true, // page exits (needed for bounce analysis)
    disable_session_recording: false, // keep session replay on
    session_recording: {
        maskAllInputs: true, // mask all form input values
        recordHeaders: false, // capture network request/response headers in replays
        recordBody: false // capture network request/response bodies in replays
    }
};

// PostHog initializes lazily here (not at app boot), so it only ever runs for
// logged-in researchers — never participants or logged-out visitors.
let posthogStarted = false;

const analytics = {
    init(token) {
        if (skipTracking) {
            return;
        }
        mixpanel.init(token);
    },
    track(key, options) {
        if (skipTracking) {
            console.log('analytics.track - skipping local', key, options);
            return;
        }
        return mixpanel.track(key, options);
    },
    // Identify a logged-in user across analytics tools. Mixpanel tracks in
    // production builds; PostHog session replay runs (and initializes) only when
    // recording is enabled for this environment (see posthogShouldRecord).
    identify(user) {
        if (!skipTracking) {
            mixpanel.identify(user.id);
            mixpanel.people.set({ $email: user.email, $name: user.name });
        }
        if (posthogShouldRecord()) {
            if (!posthogStarted) {
                posthog.init(config.POSTHOG_TOKEN, posthogOptions);
                posthogStarted = true;
            }
            posthog.identify(user.id, { email: user.email, name: user.name });
        }
    },
    // Clear the user's identity on logout so sessions don't bleed across
    // users on a shared browser.
    posthogReset() {
        if (posthogStarted) {
            posthog.reset();
        }
    },
    pendoInitialize({ visitor, account }) {
        if (skipTracking) {
            console.log('analytics.pendoInitialize - skipping local', visitor, account);
            return;
        }
        if (window.pendo) {
            window.pendo.initialize({
                visitor,
                account
            });
        }
    }
};

export default analytics;
